import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { BookOpen, Check, Copy, KeyRound, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { useReports } from '@/admin/hooks/use-reports'
import { absolutePublicApiUrl } from '@/admin/lib/public-api-url'

/**
 * "Connect Power BI" - how to pull the tenant's report datasets into a BI tool.
 *
 * Everything shown is data: the dataset list is the tenant's own report
 * catalogue (GET /api/reports/, the same list the Reports panel renders) and
 * the URLs point at the generic Public API report feed. Gated by the caller on
 * the `public_api` capability flag, which also gates that feed.
 */
export function PowerBiConnectDialog({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: reports, isPending, isError } = useReports()
  const [copiedKey, setCopiedKey] = useState<string | null>(null)

  const datasetUrl = (key: string) =>
    absolutePublicApiUrl(`/api/v1/reports/${key}/?page_size=200`, window.location)

  async function copy(key: string) {
    try {
      await navigator.clipboard.writeText(datasetUrl(key))
      setCopiedKey(key)
      toast.success(t('powerbi.url_copied', 'Dataset URL copied'))
      setTimeout(() => setCopiedKey((current) => (current === key ? null : current)), 2000)
    } catch {
      toast.error(t('powerbi.copy_failed', 'Could not copy - select the URL and copy it manually'))
    }
  }

  function go(to: string) {
    onClose()
    navigate(to)
  }

  const steps = [
    t('powerbi.step_key', 'Create an API key in Settings - API keys (it inherits your report access).'),
    t('powerbi.step_get_data', 'In Power BI choose Get Data - Web - Advanced and paste a dataset URL below.'),
    t('powerbi.step_header', 'Add the header Authorization with the value Token followed by your API key.'),
    t('powerbi.step_refresh', 'Publish the report and turn on Scheduled refresh to keep it up to date.'),
  ]

  return (
    <Dialog open={open} onOpenChange={(value) => { if (!value) onClose() }}>
      <DialogContent className="sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle>{t('powerbi.title', 'Connect Power BI')}</DialogTitle>
          <DialogDescription>
            {t(
              'powerbi.description',
              'Your standard reports are published as datasets Power BI can pull and refresh on a schedule.',
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5">
          <ol className="flex flex-col gap-2">
            {steps.map((step, index) => (
              <li key={step} className="flex gap-3 text-[13px] leading-relaxed text-[var(--ink-2)]">
                <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[var(--bg-subtle)] text-[11px] font-medium text-[var(--ink)]">
                  {index + 1}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>

          <div className="flex flex-col gap-2">
            <span className="text-[12px] font-medium uppercase tracking-[0.08em] text-[var(--ink-3)]">
              {t('powerbi.datasets', 'Datasets')}
            </span>
            {isPending ? (
              <div className="flex items-center gap-2 py-4 text-[13px] text-[var(--ink-3)]">
                <Loader2 className="animate-spin" size={14} />
                {t('powerbi.loading', 'Loading datasets...')}
              </div>
            ) : isError ? (
              <div className="py-4 text-[13px] text-[var(--status-error)]">
                {t('reports.load_error', 'Could not load reports.')}
              </div>
            ) : (reports ?? []).length === 0 ? (
              <div className="py-4 text-[13px] text-[var(--ink-3)]">
                {t('powerbi.no_datasets', 'No report datasets are configured yet.')}
              </div>
            ) : (
              <div className="flex max-h-[280px] flex-col gap-2 overflow-y-auto">
                {(reports ?? []).map((report) => (
                  <div
                    key={report.key}
                    className="flex items-center gap-3 rounded-[10px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 py-2.5"
                  >
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className="flex items-center gap-2 text-[13px] font-medium text-[var(--ink)]">
                        {report.title}
                        {report.requires_finance && (
                          <Badge variant="outline">{t('reports.finance_badge', 'Finance')}</Badge>
                        )}
                      </span>
                      <code className="truncate font-mono text-[11px] text-[var(--ink-3)]">
                        {datasetUrl(report.key)}
                      </code>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => void copy(report.key)}
                      aria-label={t('powerbi.copy_url', 'Copy URL')}
                    >
                      {copiedKey === report.key ? <Check size={14} /> : <Copy size={14} />}
                      {t('powerbi.copy_url', 'Copy URL')}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex-wrap gap-2 sm:justify-between">
          <Button variant="ghost" onClick={() => go('/admin/developers?guide=08-powerbi-reports')}>
            <BookOpen size={14} />
            {t('powerbi.guide', 'Setup guide & data model')}
          </Button>
          <Button onClick={() => go('/settings/api-keys')}>
            <KeyRound size={14} />
            {t('powerbi.create_key', 'Create an API key')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
